import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useMainContext } from './MainProvider';

import { FEATURE_FLAGS } from '~common';
import { useQueryGetCurrentSubscription } from '~hooks/useQueryGetCurrentSubscription';
import { useQueryGetUserSubscriptionByPhoneNumber } from '~hooks/useQueryGetUserSubscriptionByPhoneNumber';
import {
  SubscriptionStatus,
  UserPassState,
  UserPassSummary,
  UserSubscription,
} from '~types/subscription.type';
import { checkEnabledFeatureFlag } from '~utils';

type UserSubscriptionContextType = {
  activeSubscription: UserSubscription | null;
  /**
   * True only while the first fetch is in flight, never on a background
   * refetch. Consumers gate whole subtrees on this, so reporting every refetch
   * would unmount the screen the user is standing on — and take any open
   * dialog with it.
   */
  isLoadingActiveSubscription: boolean;
  /**
   * What to render for this user, per `GET /api/subscription/current`.
   *
   * Falls back to `USABLE` / `NONE` derived from the legacy by-phone-number
   * payload while the pass flag is off, so callers can branch on one field.
   */
  subscriptionStatus: SubscriptionStatus;
  /**
   * The hold behind the running period. `null` for a legacy period that
   * belongs to no pass — there is nothing to put on a pass screen for those.
   */
  activePass: UserPassSummary | null;
  isPassCanceled: boolean;
  refetchSubscription: () => void;
};

const UserSubscriptionContext = createContext<UserSubscriptionContextType>({
  activeSubscription: null,
  isLoadingActiveSubscription: false,
  subscriptionStatus: SubscriptionStatus.NONE,
  activePass: null,
  isPassCanceled: false,
  refetchSubscription: () => {
    return;
  },
});

export const UserSubscriptionProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { phoneNumber, cachedPhoneNumber } = useMainContext();
  const [searchParams] = useSearchParams();

  const isSubscriptionPassEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.WEB_APP_SUBSCRIPTION_PASS,
  );

  // Passing the station lets the backend also check the period is redeemable
  // here, so we never offer a swap that eject would refuse.
  const imei = searchParams.get('box') || undefined;

  const queryGetCurrentSubscription = useQueryGetCurrentSubscription(
    { imei },
    { enabled: isSubscriptionPassEnabled },
  );

  const queryGetUserSubscription = useQueryGetUserSubscriptionByPhoneNumber(
    { phoneNumber: cachedPhoneNumber || phoneNumber || '' },
    { enabled: !isSubscriptionPassEnabled },
  );

  const providerValues = useMemo(() => {
    if (!isSubscriptionPassEnabled) {
      const subscription = queryGetUserSubscription.data?.subscription || null;

      return {
        activeSubscription: subscription,
        isLoadingActiveSubscription:
          queryGetUserSubscription.isLoading &&
          queryGetUserSubscription.isFetching,
        subscriptionStatus: subscription
          ? SubscriptionStatus.USABLE
          : SubscriptionStatus.NONE,
        activePass: null,
        isPassCanceled: false,
        refetchSubscription: queryGetUserSubscription.refetch,
      };
    }

    const data = queryGetCurrentSubscription.data;
    const pass = data?.pass ?? null;

    return {
      activeSubscription: data?.subscription ?? null,
      isLoadingActiveSubscription:
        queryGetCurrentSubscription.isLoading &&
        queryGetCurrentSubscription.isFetching,
      subscriptionStatus: data?.status ?? SubscriptionStatus.NONE,
      activePass: pass,
      isPassCanceled: pass?.state === UserPassState.CANCELED,
      refetchSubscription: queryGetCurrentSubscription.refetch,
    };
  }, [
    isSubscriptionPassEnabled,
    queryGetCurrentSubscription.data,
    queryGetCurrentSubscription.isLoading,
    queryGetCurrentSubscription.isFetching,
    queryGetCurrentSubscription.refetch,
    queryGetUserSubscription.data,
    queryGetUserSubscription.isLoading,
    queryGetUserSubscription.isFetching,
    queryGetUserSubscription.refetch,
  ]);

  return (
    <UserSubscriptionContext.Provider value={providerValues}>
      {children}
    </UserSubscriptionContext.Provider>
  );
};

export const useUserSubscriptionContext = () =>
  useContext(UserSubscriptionContext);
